
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Trash2, Plus, Edit } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface PointsRule {
  id: string;
  action_type: string;
  action_name: string;
  action_name_en?: string;
  points: number;
  description: string;
  description_en?: string;
  is_active: boolean;
  is_repeatable: boolean;
  created_at: string;
}

const PointsSettings = () => {
  const [pointsRules, setPointsRules] = useState<PointsRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PointsRule | null>(null);
  const [formData, setFormData] = useState({
    action_type: '',
    action_name: '',
    action_name_en: '',
    points: 0,
    description: '',
    description_en: '',
    is_active: true,
    is_repeatable: false,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchPointsRules();
  }, []);

  const fetchPointsRules = async () => {
    try {
      const { data, error } = await supabase
        .from('points_rules')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPointsRules(data || []);
    } catch (error) {
      console.error('Error fetching points rules:', error);
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: '無法載入積分規則',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingRule) {
        const { error } = await supabase
          .from('points_rules')
          .update(formData)
          .eq('id', editingRule.id);
        
        if (error) throw error;
        
        toast({
          title: '更新成功',
          description: '積分規則已更新',
        });
      } else {
        const { error } = await supabase
          .from('points_rules')
          .insert([formData]);
        
        if (error) throw error;
        
        toast({
          title: '新增成功',
          description: '積分規則已新增',
        });
      }

      setIsDialogOpen(false);
      setEditingRule(null);
      setFormData({
        action_type: '',
        action_name: '',
        action_name_en: '',
        points: 0,
        description: '',
        description_en: '',
        is_active: true,
        is_repeatable: false,
      });
      fetchPointsRules();
    } catch (error) {
      console.error('Error saving points rule:', error);
      toast({
        variant: 'destructive',
        title: '儲存失敗',
        description: '無法儲存積分規則',
      });
    }
  };

  const handleEdit = (rule: PointsRule) => {
    setEditingRule(rule);
    setFormData({
      action_type: rule.action_type,
      action_name: rule.action_name,
      action_name_en: rule.action_name_en || '',
      points: rule.points,
      description: rule.description,
      description_en: rule.description_en || '',
      is_active: rule.is_active,
      is_repeatable: rule.is_repeatable,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此積分規則嗎？')) return;

    try {
      const { error } = await supabase
        .from('points_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: '刪除成功',
        description: '積分規則已刪除',
      });
      fetchPointsRules();
    } catch (error) {
      console.error('Error deleting points rule:', error);
      toast({
        variant: 'destructive',
        title: '刪除失敗',
        description: '無法刪除積分規則',
      });
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('points_rules')
        .update({ is_active: !isActive })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: '更新成功',
        description: `積分規則已${!isActive ? '啟用' : '停用'}`,
      });
      fetchPointsRules();
    } catch (error) {
      console.error('Error toggling rule status:', error);
      toast({
        variant: 'destructive',
        title: '更新失敗',
        description: '無法更新積分規則狀態',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">積分條件設定</h1>
          <p className="text-gray-600 dark:text-gray-400">管理積分獲得條件與規則</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingRule(null);
              setFormData({
                action_type: '',
                action_name: '',
                action_name_en: '',
                points: 0,
                description: '',
                description_en: '',
                is_active: true,
                is_repeatable: false,
              });
            }}>
              <Plus className="mr-2 h-4 w-4" />
              新增積分規則
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRule ? '編輯積分規則' : '新增積分規則'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="action_type">動作類型</Label>
                <Select value={formData.action_type} onValueChange={(value) => setFormData({...formData, action_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="選擇動作類型" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="review_submit">評論投稿</SelectItem>
                    <SelectItem value="photo_upload">上傳照片</SelectItem>
                    <SelectItem value="profile_complete">完善資料</SelectItem>
                    <SelectItem value="weekly_login">每週登入</SelectItem>
                    <SelectItem value="share_click">分享點擊</SelectItem>
                    <SelectItem value="referral_bonus">推薦獎勵</SelectItem>
                    <SelectItem value="special_event">特殊活動</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="action_name">動作名稱（中文）</Label>
                <Input
                  id="action_name"
                  value={formData.action_name}
                  onChange={(e) => setFormData({...formData, action_name: e.target.value})}
                  placeholder="輸入動作名稱"
                  required
                />
              </div>

              <div>
                <Label htmlFor="action_name_en">動作名稱（英文）</Label>
                <Input
                  id="action_name_en"
                  value={formData.action_name_en}
                  onChange={(e) => setFormData({...formData, action_name_en: e.target.value})}
                  placeholder="Enter action name in English"
                />
              </div>

              <div>
                <Label htmlFor="points">積分數量</Label>
                <Input
                  id="points"
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({...formData, points: parseInt(e.target.value) || 0})}
                  placeholder="輸入積分數量"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">說明（中文）</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="輸入積分規則說明"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="description_en">說明（英文）</Label>
                <Textarea
                  id="description_en"
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                  placeholder="Enter description in English"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="is_active">啟用此規則</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_repeatable"
                  checked={formData.is_repeatable}
                  onChange={(e) => setFormData({...formData, is_repeatable: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="is_repeatable">可重複獲得</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingRule ? '更新' : '新增'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  取消
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>積分規則列表</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {pointsRules.length === 0 ? (
              <p className="text-gray-500 text-center py-8">尚無積分規則</p>
            ) : (
              pointsRules.map((rule) => (
                <div key={rule.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{rule.action_name}</h3>
                      {rule.action_name_en && (
                        <span className="text-sm text-gray-500">({rule.action_name_en})</span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded ${
                        rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rule.is_active ? '啟用' : '停用'}
                      </span>
                      {rule.is_repeatable && (
                        <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                          可重複
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{rule.description}</p>
                    {rule.description_en && (
                      <p className="text-sm text-gray-500 mt-1 italic">{rule.description_en}</p>
                    )}
                    <p className="text-sm font-medium text-blue-600 mt-1">+{rule.points} 積分</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(rule.id, rule.is_active)}
                    >
                      {rule.is_active ? '停用' : '啟用'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(rule)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(rule.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PointsSettings;
